import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { PostsGroupHeaderDocument, UpdatePostsGroupDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import GroupForm from 'modules/dashboard/components/GroupForm'

interface Props {
  onClose: () => void
  groupId: number
}

function EditGroup({ onClose, groupId }: Props) {
  const { data, loading } = useQuery(PostsGroupHeaderDocument, {
    variables: {
      id: groupId,
    },
  })

  const group = data?.posts.group

  const form = React.useRef(
    new Form(
      {
        title: group?.title ?? '',
        description: group?.description ?? '',
      },
      {
        title: 'string',
        description: 'string',
      }
    )
  ).current

  React.useEffect(() => {
    if (group) {
      form.setValues({
        title: group.title ?? '',
        description: group.description ?? '',
      })
    }
  }, [group, form])

  const [updateGroup] = useMutation(UpdatePostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.updatePostsGroup,
  })

  const submit = async (closePortal: ClosePortalFn) => {
    if (!group) return

    const result = await updateGroup({
      variables: {
        id: groupId,
        title: form.get('title').value,
        description: form.get('description').value,
      },
      refetchQueries: [
        {
          query: PostsGroupHeaderDocument,
          variables: { id: groupId },
        },
      ],
    })

    const handled = handleResult(result, (_errors) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.error_message'),
      })
    })

    if (handled.success) {
      closePortal()
    }
  }

  if (loading) return <Loading />

  if (!group) return null

  const disabled = !form.get('title').value || !form.get('description').value

  return (
    <Modal
      description={i18n.t('dashboard.groups.edit.description')}
      illustration="people-add"
      onClose={onClose}
      onSubmit={submit}
      title={i18n.t('dashboard.groups.edit.title')}
      footer={
        <Button disabled={disabled} submit>
          {i18n.t('dashboard.groups.edit.title')}
        </Button>
      }
    >
      <GroupForm form={form} />
    </Modal>
  )
}

export default observer(EditGroup)
